<template>
  <div id="app" :data-theme="theme">
    <AppBar :theme="theme" @toggle-theme="toggleTheme" />
    <HeroSection />
    <ExplorerSection />
    <!-- <CitationSection /> -->
    <FooterSection />
  </div>
</template>

<script>
import AppBar from "./components/AppBar.vue";
import HeroSection from "./components/Header.vue";
import ExplorerSection from "./components/MarvelGrid.vue";
// import CitationSection from "./components/Citation.vue";
import FooterSection from "./components/Footer.vue";

export default {
  name: "App",
  metaInfo: {
    title: 'MARVEL-40M+ | Multi-Level 3D Captioning Dataset | CVPR 2025',
    meta: [
      { name: 'description', content: 'MARVEL-40M+ is a large-scale multi-level 3D captioning dataset with 40M+ annotations across 8.9M 3D objects from 7 source datasets. Presented at CVPR 2025.' },
      { name: 'keywords', content: 'MARVEL-40M, 3D captioning dataset, text-to-3D, CVPR 2025, Objaverse, ShapeNet' },
      { property: 'og:title', content: 'MARVEL-40M+ | Multi-Level 3D Captioning Dataset' },
      { property: 'og:description', content: '40M+ multi-level captions across 8.9M 3D objects. CVPR 2025.' },
      { property: 'og:url', content: 'https://sadilkhan.github.io/Marvel-Explorer/' },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'MARVEL-40M+ Dataset Explorer' },
    ],
    link: [
      { rel: 'canonical', href: 'https://sadilkhan.github.io/Marvel-Explorer/' }
    ],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'MARVEL-40M+',
          description: 'Multi-Level Visual Elaboration for High-Fidelity Text-to-3D Content Creation.',
          url: 'https://sadilkhan.github.io/Marvel-Explorer/',
          creator: { '@type': 'Person', name: 'Mohammad Sadil Khan' },
          keywords: ['3D captioning', 'text-to-3D', 'CVPR 2025'],
        }
      }
    ]
  },
  components: { AppBar, HeroSection, ExplorerSection, FooterSection },
  data() {
    return {
      theme: localStorage.getItem('marvel-theme') || 'dark',
    };
  },
  methods: {
    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('marvel-theme', this.theme);
    },
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Syne:wght@400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500&family=Google+Sans:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

/* ── DARK MODE (default) ── */
:root,
[data-theme="dark"] {
  --bg:        #03050f;
  --bg2:       #070c1a;
  --surface:   rgba(255,255,255,0.04);
  --surface2:  rgba(255,255,255,0.08);
  --border:    rgba(255,255,255,0.08);
  --border2:   rgba(255,255,255,0.14);
  --text:      #f0f4ff;
  --text2:     #8b9dbf;
  --text3:     #4a5a7a;
  --accent:    #5b7fff;
  --accent2:   #c77dff;
  --accent3:   #00bcd4;
  --glow:      rgba(91,127,255,0.35);
  --glow2:     rgba(199,125,255,0.25);
  --radius:    16px;
  --transition: 0.3s cubic-bezier(0.4,0,0.2,1);
  --nav-bg:    rgba(3,5,15,0.85);
  --card-bg:   rgba(255,255,255,0.04);
  --shadow:    rgba(0,0,0,0.5);
}

/* ── LIGHT MODE ── */
[data-theme="light"] {
  --bg:        #f4f6fb;
  --bg2:       #eaecf5;
  --surface:   rgba(255,255,255,0.7);
  --surface2:  rgba(255,255,255,0.9);
  --border:    rgba(30,40,80,0.1);
  --border2:   rgba(30,40,80,0.18);
  --text:      #0f1630;
  --text2:     #3d4f7c;
  --text3:     #7a8cb0;
  --accent:    #3d5af1;
  --accent2:   #9b3de8;
  --accent3:   #0097a7;
  --glow:      rgba(61,90,241,0.2);
  --glow2:     rgba(155,61,232,0.15);
  --nav-bg:    rgba(244,246,251,0.92);
  --card-bg:   rgba(255,255,255,0.9);
  --shadow:    rgba(15,22,48,0.1);
}

html { scroll-behavior: smooth; }

body {
  font-family: 'Google Sans', 'Syne', sans-serif;
  background: var(--bg);
  color: var(--text);
  overflow-x: hidden;
  min-height: 100vh;
  transition: background 0.4s ease, color 0.4s ease;
}

#app {
  background: var(--bg);
  min-height: 100vh;
  transition: background 0.4s ease;
}

/* Vuetify overrides */
.v-application { background: transparent !important; }
.v-application--wrap { background: transparent !important; min-height: unset !important; }

/* scrollbar */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: var(--bg); }
::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 3px; }

/* selection */
::selection { background: rgba(91,127,255,0.25); color: var(--text); }

/* Global utility */
.glass {
  background: var(--surface);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--border);
}

/* Reveal animation */
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}
/* ── Global button reset ── */
.cta-primary, .cta-secondary, .submit-btn, .nav-btn, .dataset-tab.active {
  transition: all var(--transition);
}
</style>